import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";
import { toWords } from "number-to-words";
import api from "../Components/Services/Api";

const round2 = (num) => Math.round((num + Number.EPSILON) * 100) / 100;

export const generateInvoicePDF = async (order) => {
  const {
    orderNumber,
    cart = [],
    date,
    additionalCharges: orderCharges = [],
    taxes = [],
  } = order;

  // Fetch Vendor Details
  const cartVendorId = cart?.[0]?.vendorId;
  let vendor = {};
  try {
    const { data } = await api.get(`/getVendor/${cartVendorId}`);
    vendor = data || {};
  } catch (err) {
    console.error("Vendor fetch error:", err);
  }

  const {
    vendorName = "N/A",
    vendorAddress = "N/A",
    gstIn = "N/A",
    mobile= "N/A",
    state = "N/A",
  } = vendor;

  const doc = new jsPDF("p", "mm", "a4");
  const invoiceDate = moment(date).format("DD-MM-YYYY");

  /* ---------------- HEADER ---------------- */
  doc.setFont("helvetica", "bold").setFontSize(16);
  doc.text("DZYRIS FROZEN FOODS PVT. LTD.", 14, 15);
  doc.setFont("helvetica", "normal").setFontSize(10);
  doc.text("SHOP NO.8, ARIHANT INDUSTRIAL ESTATE,", 14, 21);
  doc.text("KAMAN BHIWANDI ROAD, MORI GAON", 14, 26);
  doc.text("GSTIN: 27AAJCD8219Q1ZW | State Code: 27", 14, 31);
  doc.text("CIN: U15549MH2023PTC397586", 14, 36);
  doc.text("Contact: 9930973001 | Email: accounts@dzyris.in", 14, 41);

  /* ---------------- INVOICE INFO ---------------- */
  doc.setFont("helvetica", "bold").text("Purchase Order No.:", 14, 50);
  doc.setFont("helvetica", "normal").text(orderNumber || "N/A", 60, 50);
  doc.setFont("helvetica", "bold").text("Dated:", 140, 50);
  doc.setFont("helvetica", "normal").text(invoiceDate, 155, 50);

  /* ---------------- BILL TO ---------------- */
  doc.setFont("helvetica", "bold").text("Bill To:", 14, 58);
  doc.setFont("helvetica", "normal");
  doc.text(vendorName, 14, 63);
  doc.text(vendorAddress, 14, 68, { maxWidth: 180 });
  doc.text(`State: ${state}`, 14, 73);
  doc.text(`GSTIN: ${gstIn}`, 14, 78);
  doc.text(`Contact: ${mobile}`, 14, 83);

  /* ---------------- TABLE ---------------- */
  const tableColumn = [
    "Sr No",
    "Item Description",
    "Qty",
    "Rate (Inc. Tax)",
    "Rate (Exc. Tax)",
    "Amount",
  ];

  const taxesArray = Array.isArray(taxes) ? taxes : [];
  const cgstTax = taxesArray.find((t) => t.gstType === "CGST") || { rate: 0 };
  const sgstTax = taxesArray.find((t) => t.gstType === "SGST") || { rate: 0 };
  const igstTax = taxesArray.find((t) => t.gstType === "IGST") || { rate: 0 };

  const cgstRate = Number(cgstTax.rate || 0);
  const sgstRate = Number(sgstTax.rate || 0);
  const igstRate = Number(igstTax.rate || 0);

  const tableRows = cart.map((item, index) => {
    const qty = Number(item.quantity) || 0;
    const incRate = Number(item.price) || 0;
    const excRate = round2(
      incRate / (1 + (cgstRate + sgstRate + igstRate) / 100)
    );
    const amount = round2(qty * excRate);
    return [
      index + 1,
      item.name || "",
      qty,
      incRate.toFixed(2),
      excRate.toFixed(2),
      amount.toFixed(2),
    ];
  });

  doc.autoTable({
    startY: 90,
    head: [tableColumn],
    body: tableRows,
    styles: {
      fontSize: 9,
      cellPadding: 3,
      halign: "center",
      valign: "middle",
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: [0, 0, 0],
      textColor: 255,
      halign: "center",
    },
    tableWidth: "auto",
    margin: { left: 10, right: 10 }, // full width with small margin
    theme: "grid",
  });

  let finalY = doc.lastAutoTable.finalY + 10;

  /* ---------------- TOTALS ---------------- */
  const totalBeforeTax = round2(
    cart.reduce((sum, item) => {
      const excRate = round2(
        item.price / (1 + (cgstRate + sgstRate + igstRate) / 100)
      );
      return sum + excRate * item.quantity;
    }, 0)
  );

  const cgst = round2((totalBeforeTax * cgstRate) / 100);
  const sgst = round2((totalBeforeTax * sgstRate) / 100);
  const igst = round2((totalBeforeTax * igstRate) / 100);

  const additionalTotal = round2(
    orderCharges.reduce((sum, ch) => sum + (Number(ch.amount) || 0), 0)
  );

  const grandTotalRaw = totalBeforeTax + cgst + sgst + igst + additionalTotal;
  const grandTotal = Math.round(grandTotalRaw);

  doc.setFont("helvetica", "bold").setFontSize(10);
  doc.text("Total (Excl. Tax):", 140, finalY);
  doc.text(`${totalBeforeTax.toFixed(2)}`, 190, finalY, { align: "right" });

  let y = finalY;

  if (cgst > 0) {
    y += 6;
    doc.text(`CGST (${cgstRate}%):`, 140, y);
    doc.text(`${cgst.toFixed(2)}`, 190, y, { align: "right" });
  }

  if (sgst > 0) {
    y += 6;
    doc.text(`SGST (${sgstRate}%):`, 140, y);
    doc.text(`${sgst.toFixed(2)}`, 190, y, { align: "right" });
  }

  if (igst > 0) {
    y += 6;
    doc.text(`IGST (${igstRate}%):`, 140, y);
    doc.text(`${igst.toFixed(2)}`, 190, y, { align: "right" });
  }

  orderCharges.forEach((ch) => {
    y += 6;
    doc.text(`${ch.label}:`, 140, y);
    doc.text(`${Number(ch.amount).toFixed(2)}`, 190, y, { align: "right" });
  });

  y += 8;
  doc.setFontSize(12).setFont("helvetica", "bold");
  doc.text("Grand Total:", 140, y);
  doc.text(`${grandTotal.toFixed(2)}`, 190, y, { align: "right" });

  /* ---------------- AMOUNT IN WORDS ---------------- */
  y += 10;
  doc.setFont("helvetica", "normal").setFontSize(10);
  doc.text(
    `Amount in words: Rupees ${toWords(grandTotal)
      .replace(/^\w/, (c) => c.toUpperCase())} only.`,
    14,
    y,
    { maxWidth: 180 }
  );

  /* ---------------- FOOTER ---------------- */
  y += 15;
  doc.setFontSize(9).setFont("helvetica", "italic");
  doc.text("Declaration:", 14, y);
  doc.text(
    "We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.",
    14,
    y + 5,
    { maxWidth: 180 }
  );
  doc.text("Interest would be charged on overdue amounts.", 14, y + 10);

  doc.setFont("helvetica", "bold").setFontSize(10).text("Bank Details:", 14, y + 18);
  doc.setFont("helvetica", "normal").setFontSize(9);
  doc.text("A/C Name: DZYRIS FROZEN FOODS PVT.LTD.", 14, y + 23);
  doc.text("A/C No: 19510210005415", 14, y + 28);
  doc.text("IFSC: UCBA0001951", 14, y + 33);
  doc.text("Branch: NALASOPARA", 14, y + 38);
  doc.text("This is a computer generated copy.", 14, y + 48);

  doc.save(`${orderNumber}.pdf`);
};
